import { useQuery, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS } from '../config/api-endpoints';
import authService from '../services/authService';

export interface ClassFeedback {
  id: number;
  class_id: number;
  title: string;
  description: string;
  is_active: number;
  created_at: string;
}

export interface StudentCheck {
  id: number;
  course_id: number;
  class_title: string;
}

export interface FeedbackQuestion {
  id: number;
  feedback_id: number;
  question_type: string;
  question_text: string;
  options?: string[];
}

export interface FeedbackResponseData {
  feedback_id: number;
  user_id: number;
  responses: string[];
}

export interface UserSubmittedCheck {
  data: boolean;
  status: string;
}

// Hook to get class feedback
export function useClassFeedback(classId?: string | number, userId?: string | number) {
  return useQuery({
    queryKey: ['classFeedback', classId, userId],
    queryFn: async () => {
      const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
      const url = `${baseURL}${API_ENDPOINTS.GET_CLASS_FEEDBACK(classId!.toString(), userId!.toString())}`;
      
      console.log('Fetching class feedback for class ID:', classId);
      console.log('Full URL:', url);
      
      const res: any = await (await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`,
        },
      })).json();
      
      console.log('Class Feedback API Response:', res);

      if (res.error) {
        console.error('Class feedback error:', res.error);
        throw new Error('Failed to fetch class feedback');
      }
      
      // Return the data array or single item
      const feedbackData = res.data || res;
      console.log('Processed Class Feedback Data:', feedbackData);
      return feedbackData as ClassFeedback[];
    },
    enabled: !!classId && !!userId,
  });
}

// Hook to get student check data
export function useStudentCheck(studentId: string | number) {
  return useQuery({
    queryKey: ['studentCheck', studentId],
    queryFn: async () => {
      const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
      const url = `${baseURL}${API_ENDPOINTS.GET_STUDENT_CHECK(studentId.toString())}`;
      
      console.log('Fetching student check for student ID:', studentId);
      console.log('Full URL:', url);
      
      const res: any = await (await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`,
        },
      })).json();
      
      console.log('Student Check API Response:', res);
      
      if (res.error) {
        console.error('Student check error:', res.error);
        throw new Error('Failed to fetch student check data');
      }
      
      // Return the data
      const studentData = res.data || res;
      console.log('Processed Student Check Data:', studentData);
      return studentData as StudentCheck;
    },
    enabled: !!studentId,
  });
}

// Hook to get feedback questions
export function useFeedbackQuestions(feedbackId: string | number) {
  return useQuery({
    queryKey: ['feedbackQuestions', feedbackId],
    queryFn: async () => {
      const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
      const url = `${baseURL}${API_ENDPOINTS.GET_FEEDBACK_QUESTIONS(feedbackId.toString())}`;
      
      console.log('Fetching feedback questions for feedback ID:', feedbackId);
      console.log('Full URL:', url);
      
      const res: any = await (await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`,
        },
      })).json();
      
      console.log('Feedback Questions API Response:', res);
      
      if (res.error) {
        console.error('Feedback questions error:', res.error);
        throw new Error('Failed to fetch feedback questions');
      }
      
      // Return the data array
      const questionsData = res.data || res;
      console.log('Processed Feedback Questions Data:', questionsData);
      return questionsData as FeedbackQuestion[];
    },
    enabled: !!feedbackId,
  });
}

// Function to submit feedback response
export const submitFeedbackResponse = async (responseData: FeedbackResponseData, queryClient?: any) => {
  const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
  const url = `${baseURL}${API_ENDPOINTS.CREATE_FEEDBACK_RESPONSE}`;
  
  console.log('Submitting feedback response:', responseData);
  console.log('Full URL:', url);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authService.getToken()}`,
    },
    body: JSON.stringify(responseData),
  });

  const result = await response.json();
  
  console.log('Feedback Response API Result:', result);
  
  if (!response.ok || result.error) {
    throw new Error(result.message || 'Failed to submit feedback response');
  }
  
  // Invalidate the user submitted feedback query to refetch the latest status
  if (queryClient) {
    queryClient.invalidateQueries(['userSubmittedFeedback']);
  }
  
  return result.data || result;
};

// Hook to check if user has already submitted feedback
export function useUserSubmittedFeedback(userId: string | number, feedbackId: string | number) {
  return useQuery({
    queryKey: ['userSubmittedFeedback', userId, feedbackId],
    queryFn: async () => {
      const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
      const url = `${baseURL}${API_ENDPOINTS.CHECK_USER_SUBMITTED_FEEDBACK(userId.toString(), feedbackId.toString())}`;
      
      console.log('Checking if user submitted feedback - User ID:', userId, 'Feedback ID:', feedbackId);
      console.log('Full URL:', url);
      
      const res: any = await (await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`,
        },
      })).json();
      
      console.log('User Submitted Feedback API Response:', res);
      
      if (res.error) {
        console.error('User submitted feedback check error:', res.error);
        throw new Error('Failed to check if user submitted feedback');
      }
      
      // Return the data
      const submittedData = res.data !== undefined ? res.data : res;
      console.log('Processed User Submitted Data:', submittedData);
      return submittedData as boolean;
    },
    enabled: !!(userId && feedbackId),
  });
}